import { useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';
import { InferType, mixed, number, object, ObjectSchema, string } from 'yup';

import { AssetClass, BaseSpaceReadModel, CommonAreaType, ParkingSpotType, RoomType, SpaceDiscriminator } from '@client/api';
import {
  AreaAdornment,
  Button,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  i18n,
  Input,
  isDefinedString,
  Modal,
  ModalContent,
  ModalFooter,
  ParentSpaceAutocomplete,
  PriceAutocomplete,
  Select,
  transformToOptionalNumber,
  Typography,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

const COMMON_AREA_TYPE_OPTIONS: CommonAreaType[] = ['Shared', 'Building', 'Communication', 'TechnicalOperation'];
const ROOM_TYPE_OPTIONS: RoomType[] = [
  'Bedroom',
  'LivingRoom',
  'Kitchen',
  'Bathroom',
  'Toilet',
  'StorageRoom',
  'Office',
  'Other',
];
const PARKING_SPOT_TYPE_OPTIONS: ParkingSpotType[] = ['Garage', 'Indoor', 'OutdoorCovered', 'OutdoorUncovered'];
const ASSET_CLASS_OPTIONS: AssetClass[] = ['Office', 'RetailHighStreet', 'Retail', 'Logistics', 'Industrial', 'Hotel', 'Healthcare', 'Education', 'Mixed'];

type FormModel = {
  name: string;
  description?: string | null;
  areaMetric?: number | null;
  discriminator?: SpaceDiscriminator | null;
  rentPriceId?: string | null;
  operationalCostPriceId?: string | null;
  commonAreaType?: CommonAreaType | null;
  roomType?: RoomType | null;
  parkingSpotType?: ParkingSpotType | null;
  assetClass?: AssetClass | null;
  parent?: BaseSpaceReadModel | null;
};

const schema: ObjectSchema<FormModel> = object({
  name: string().default(''),
  description: string().nullable(),
  areaMetric: number().min(0).transform(transformToOptionalNumber).required(),
  discriminator: mixed<SpaceDiscriminator>().nullable(),
  // COMMENTED OUT: Standard leie - uncomment to restore
  // rentPriceId: string()
  //   .nullable()
  //   .when('discriminator', {
  //     is: (x: SpaceDiscriminator) => x === 'CommercialSpace' || x === 'ResidentialUnit',
  //     then: (schema) => schema.required(),
  //     otherwise: (schema) => schema,
  //   }),
  rentPriceId: string().nullable(),
  // COMMENTED OUT: Driftskostnader - uncomment to restore
  // operationalCostPriceId: string()
  //   .nullable()
  //   .when('discriminator', {
  //     is: (x: SpaceDiscriminator) => x === 'CommonArea',
  //     then: (schema) => schema.required(),
  //     otherwise: (schema) => schema,
  //   }),
  operationalCostPriceId: string().nullable(),
  commonAreaType: string<CommonAreaType>()
    .nullable()
    .when('discriminator', {
      is: (x: SpaceDiscriminator) => x === 'CommonArea',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema,
    }),
  roomType: string<RoomType>()
    .nullable()
    .when('discriminator', {
      is: (x: SpaceDiscriminator) => x === 'Room',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema,
    }),
  parkingSpotType: string<ParkingSpotType>()
    .nullable()
    .when('discriminator', {
      is: (x: SpaceDiscriminator) => x === 'ParkingSpot',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema,
    }),
  assetClass: string<AssetClass>()
    .nullable()
    .when('discriminator', {
      is: (x: SpaceDiscriminator) => x === 'Generic' || x === 'CommercialSpace',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema,
    }),
  parent: mixed<BaseSpaceReadModel>()
    .nullable()
    .test(
      'has-defined-id',
      () => i18n.t('field.required', { ns: 'validation' }),
      (value) => {
        if (value == null) return false;
        return isDefinedString(value.id);
      },
    ),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export type SpaceFormData = FormValues;

interface SpaceFormProps {
  title: React.ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
  onClose: () => void;
  defaultValues?: FormDefaultValues;
  open?: boolean;
  isFetching?: boolean;
  create?: boolean;
}

export const SpaceForm = ({
  open = true,
  title,
  onSubmit,
  onClose,
  defaultValues: propDefaultValues,
  isFetching = false,
  create = false,
}: SpaceFormProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { buildingId, spaceId } = useParams<{ buildingId: string; spaceId: string }>();

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: propDefaultValues?.name ?? '',
      description: propDefaultValues?.description ?? '',
      areaMetric: propDefaultValues?.areaMetric ?? ('' as any),
      discriminator: propDefaultValues?.discriminator ?? undefined,
      rentPriceId: propDefaultValues?.rentPriceId ?? undefined,
      operationalCostPriceId: propDefaultValues?.operationalCostPriceId ?? undefined,
      commonAreaType: propDefaultValues?.commonAreaType ?? ('' as any),
      roomType: propDefaultValues?.roomType ?? ('' as any),
      parkingSpotType: propDefaultValues?.parkingSpotType ?? ('' as any),
      assetClass: propDefaultValues?.assetClass ?? ('' as any),
      parent: propDefaultValues?.parent ?? ('' as any),
    }),
    [propDefaultValues],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset, watch } = methods;

  const discriminator = watch('discriminator');

  useDeepCompareEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <Modal open={open} appear size="lg" onClose={onClose} onBackdropClick={onClose} title={title}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <ModalContent className="divide-solid divide-y">
            {/* Property information */}
            <article className="pt-5 pb-8 px-4 space-y-4">
              <Typography variant="label">{tGlobal('space.propertyInformation')}</Typography>
              <GridRow cols={{ def: 1, sm: 2 }} spacingX={2} spacingY={4}>
                <GridCol span={{ def: 1, sm: 2 }}>
                  <FormField<FormValues, FormPath>
                    name="name"
                    defaultValue=""
                    label={tGlobal('space.name')}
                    loading={isFetching}
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>

                <GridCol span={{ def: 1, sm: 2 }}>
                  <FormField<FormValues, FormPath>
                    name="description"
                    defaultValue=""
                    label={tGlobal('space.description')}
                    loading={isFetching}
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>

                <GridCol span={{ def: 1, sm: 1 }}>
                  <FormField<FormValues, FormPath>
                    name="areaMetric"
                    required
                    label={tGlobal('space.area')}
                    loading={isFetching}
                    render={({ field }) => (
                      <AreaAdornment>
                        <Input type="number" className="pr-10" {...field} />
                      </AreaAdornment>
                    )}
                  />
                </GridCol>

                {(discriminator === 'Generic' || discriminator === 'CommercialSpace') && (
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="assetClass"
                      required
                      defaultValue=""
                      label={tGlobal('building.assetClass')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {ASSET_CLASS_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`AssetClass.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>
                )}

                {/* COMMENTED OUT: Standard leie - uncomment to restore
                {(discriminator === 'CommercialSpace' || discriminator === 'ResidentialUnit') && (
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="rentPriceId"
                      required
                      label={tGlobal('space.rent')}
                      loading={isFetching}
                      render={({ field: { value, ref, ...field } }) => (
                        <PriceAutocomplete {...field} selected={value} />
                      )}
                    />
                  </GridCol>
                )}
                */}

                {discriminator === 'CommonArea' && (
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="commonAreaType"
                      required
                      defaultValue=""
                      label={tGlobal('commonArea.type')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {COMMON_AREA_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`CommonAreaType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>
                )}

                {/* COMMENTED OUT: Driftskostnader - uncomment to restore
                {discriminator === 'CommonArea' && (
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="operationalCostPriceId"
                      required
                      label={tGlobal('space.operationalCost')}
                      loading={isFetching}
                      render={({ field: { value, ref, ...field } }) => (
                        <PriceAutocomplete {...field} selected={value} />
                      )}
                    />
                  </GridCol>
                )}
                */}

                {discriminator === 'Room' && (
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="roomType"
                      required
                      defaultValue=""
                      label={`${tGlobal('entity.room')} ${tGlobal('room.type')}`}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {ROOM_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`RoomType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>
                )}

                {discriminator === 'ParkingSpot' && (
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="parkingSpotType"
                      required
                      defaultValue=""
                      label={tGlobal('parkingSpot.type')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {PARKING_SPOT_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`ParkingSpotType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>
                )}

                <GridCol span={{ def: 1, sm: 2 }}>
                  <FormField<FormValues, FormPath>
                    name="parent"
                    label={tGlobal('space.parent')}
                    required
                    loading={isFetching}
                    render={({ field: { value, ref, ...field } }) => (
                      <ParentSpaceAutocomplete
                        {...field}
                        selected={value}
                        rootId={buildingId}
                        spaceId={spaceId}
                        discriminator={discriminator ?? undefined}
                      />
                    )}
                  />
                </GridCol>
              </GridRow>
            </article>
          </ModalContent>

          <ModalFooter>
            <FormSubmitButton type="submit" disabled={isFetching} disableDirtyHandling={create}>
              {tCommon('save')}
            </FormSubmitButton>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('close')}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};
